import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { type ProductCardFormType } from '@web/widgets/product-card/type';
import { parameterFormGroup, sectionFormGroup } from '@web/widgets/product-card/form/product-card-widget.form';
import { ProductType, ProductUnits } from '@common/types/product-card.type';
import { type FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'web-product-detail',
    templateUrl: './product-detail.component.html',
    styleUrls: ['./product-detail.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, TranslateModule],
})
export class ProductDetailComponent {
    @Input() controls: {
        unit: ProductCardFormType['unit'];
        vendorCode: ProductCardFormType['vendorCode'];
        quantity: ProductCardFormType['quantity'];
        sections: ProductCardFormType['sections'];
        type: ProductCardFormType['type'];
        digitalProductLink: FormControl<string>;
    };

    options: ProductUnits[] = [ProductUnits.kg, ProductUnits.l, ProductUnits.pieces, ProductUnits.meter];

    public validateInputValue(value: string) {
        this.controls.quantity.setValue(this.checkMinRange(value));
    }

    private checkMinRange(value: string): number {
        if (value === '') return null;

        return +value >= 0 && value !== null ? +value : null;
    }

    public addSection(): void {
        this.controls.sections.push(sectionFormGroup());
    }

    public addParameter(index: number): void {
        this.sections.at(index).controls.parameters.push(parameterFormGroup());
    }

    public removeSection(index: number): void {
        this.sections.removeAt(index);
    }

    public removeParameter(sectionIndex: number, parameterIndex: number): void {
        this.sections.at(sectionIndex).controls.parameters.removeAt(parameterIndex);
    }

    public changeType(value: ProductType) {
        this.type.setValue(value);
        this.updateValidation();
        if (value === ProductType.digital) {
            this.controls.quantity.setValue(null);
        }
    }

    private updateValidation() {
        this.digitalProductLink.setValidators(this.isDigitalType ? [Validators.required] : null);
        this.digitalProductLink.updateValueAndValidity({ emitEvent: false });
    }

    public get sections(): ProductCardFormType['sections'] {
        return this.controls.sections as ProductCardFormType['sections'];
    }

    public get type(): ProductCardFormType['type'] {
        return this.controls.type as ProductCardFormType['type'];
    }

    public get digitalProductLink(): FormControl<string> {
        return this.controls.digitalProductLink as FormControl<string>;
    }

    public get isDigitalType(): boolean {
        return this.type.value === ProductType.digital;
    }

    public get isPhysicalType(): boolean {
        return this.type.value === ProductType.physical;
    }

    protected readonly ProductType = ProductType;
}
